import { useMutation, useSubscription } from '@apollo/client/react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from "react-qr-code";
import { GRAPHQL_ENDPOINT } from '../../../constants/graphqlEndpoint';
import { useAuth } from '../../../contexts/AuthContext';
import { AUTH_SUBSCRIPTION, BANKID_MUTATION } from '../../../integrations/allkort/graphql/gql/BankIdQueries';
import {
    BearerToken,
    CreateBankIDAuthResult,
    SubscriptionData,
    TokenByBankIdInput
} from '../../../integrations/allkort/graphql/interface/BankId';
import { handleAuthenticationTokens as handleBankIdAuthenticationTokens } from './bankIdTokenHandling';
import Button from '../../elements/Button';
import Link from '../../elements/Link';
import { BankIDLogoBlack } from '../../svgs/BankIDSVGs';
import { ExternalLinkIcon } from '../../svgs/DashboardSVGs';
import { AccountBoxIcon, MobileIcon, QrCodeIcon } from '../../svgs/ExpressonSVGs';

const sanitizeAutoStartToken = (token?: string | null): string | null => {
    if (!token) return null;
    return token.trim().replace(/}$/, '');
};

type BankIDSectionProps = {
    data?: any // TODO: Change this
};

const AUTH_SESSION_DURATION_SECONDS = 180;
const SAME_DEVICE_SESSION_KEY = 'bankid_same_device_session';

interface StoredSameDeviceSession {
    id: string;
    startTime: number;
    tokens?: BearerToken[];
}

const isBackgroundedSseFailure = (error: Error | undefined): boolean =>
    document.hidden &&
    error?.message?.toLowerCase().includes('load failed') === true;

const isIOSUserAgent = (userAgent: string) => /iPad|iPhone|iPod/.test(userAgent);
const isIOSDevice = (userAgent: string, maxTouchPoints = 0) =>
    isIOSUserAgent(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
const isMobileUserAgent = (userAgent: string, maxTouchPoints = 0) =>
    /iPad|iPhone|iPod|Android/.test(userAgent) || isIOSDevice(userAgent, maxTouchPoints);
const isChromeIOS = (userAgent: string) => /CriOS/.test(userAgent);
const isFirefoxIOS = (userAgent: string) => /FxiOS/.test(userAgent);
const isIOSNullRedirectBrowser = (userAgent: string) => /EdgiOS|OPiOS/.test(userAgent);
const isAndroidCustomSchemeBrowser = (userAgent: string) =>
    /Android/.test(userAgent) && /SamsungBrowser|EdgA|Firefox/.test(userAgent);
const getBankIdReturnUrl = (
    userAgent: string,
    maxTouchPoints: number,
    currentUrl: string
): string | undefined => {
    if (!isIOSDevice(userAgent, maxTouchPoints)) return undefined;
    if (isFirefoxIOS(userAgent)) {
        return `firefox://open-url?url=${encodeURIComponent(currentUrl)}`;
    }
    if (isChromeIOS(userAgent)) {
        return undefined;
    }
    return currentUrl;
};

const getBankIdAppUrl = (
    token: string,
    userAgent: string,
    maxTouchPoints = 0,
    returnUrl?: string
): string => {
    const appUrl = isAndroidCustomSchemeBrowser(userAgent)
        ? 'bankid:///'
        : 'https://app.bankid.com/';
    let redirectParam = '&redirect=null';

    if (isIOSDevice(userAgent, maxTouchPoints)) {
        if (isChromeIOS(userAgent)) {
            redirectParam = `&redirect=${encodeURIComponent('googlechromes:///')}`;
        } else if (isFirefoxIOS(userAgent)) {
            redirectParam = `&redirect=${encodeURIComponent('firefox://')}`;
        } else if (isIOSNullRedirectBrowser(userAgent)) {
            redirectParam = '&redirect=null';
        } else if (returnUrl) {
            redirectParam = `&redirect=${encodeURIComponent(returnUrl)}`;
        }
    }

    return `${appUrl}?autostarttoken=${token}${redirectParam}`;
};

/**
 * Login splash containing bankID auth functions
 *
 * @returns 
 */
const BankIDSection: React.FC<BankIDSectionProps> = ({ data }) => {
    // Authentication context
    const { login } = useAuth();

    // State for customer selection
    const [selectedCustomer, setSelectedCustomer] = useState<BearerToken | null>(null);
    const [showCustomerSelection, setShowCustomerSelection] = useState(false);
    const [availableTokens, setAvailableTokens] = useState<BearerToken[]>([]);

    // State for countdown timer and retry logic
    const [timeLeft, setTimeLeft] = useState<number>(AUTH_SESSION_DURATION_SECONDS);
    const [isTimeUp, setIsTimeUp] = useState<boolean>(false);
    const [authStartTime, setAuthStartTime] = useState<number | null>(null);

    // State for controlling when to show BankID authentication
    const [showBankIDAuth, setShowBankIDAuth] = useState<boolean>(false);

    // Error when launching BankID app (scheme not registered)
    const [bankIdLaunchError, setBankIdLaunchError] = useState<string | null>(null);

    // Restored session ID from sessionStorage when Android reloads the tab after BankID
    const [restoredSessionId, setRestoredSessionId] = useState<string | null>(null);

    // True when the user clicked "BankID på denna enhet" — shows waiting message instead of QR code
    const [isSameDeviceFlow, setIsSameDeviceFlow] = useState<boolean>(false);

    // iOS may abort long-lived SSE requests when BankID takes the foreground
    const [isSubscriptionPaused, setIsSubscriptionPaused] = useState<boolean>(document.hidden);

    // True when BankID returns noAccountFound — stops retry and shows info screen
    const [noAccountFound, setNoAccountFound] = useState<boolean>(false);

    const lastSameDeviceTokenRef = useRef<string | null>(null); // kept for future mobile same-device flow

    // The identifier (personnummer/orgnummer) of the person who authenticated via BankID
    const [loggedInIdentifier, setLoggedInIdentifier] = useState<string | null>(null);

    // Refs to hold current values of showCustomerSelection and isTimeUp so that
    // retryAuthentication always reads the latest value even from stale closures
    // (e.g. Apollo's onComplete callback captures the closure at subscription setup time)
    const showCustomerSelectionRef = useRef(false);
    const isTimeUpRef = useRef(false);
    const authStartTimeRef = useRef<number | null>(null);
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const noAccountFoundRef = useRef(false);
    const autoLoginStartedRef = useRef(false);
    const isSameDeviceFlowRef = useRef(false);
    const bankIdRequestInFlightRef = useRef(false);
    const [isBankIdRequestInFlight, setIsBankIdRequestInFlight] = useState(false);

    //Key auth
    const bankIdKey = import.meta.env.VITE_BANKID_KEY ?? '83740278-c4d7-42b1-86bc-090b3bf119f6';

    const handleAuthenticationTokens = (tokens: BearerToken[]) => {
        handleBankIdAuthenticationTokens(tokens, autoLoginStartedRef, {
            setAvailableTokens,
            setSelectedCustomer,
            setShowCustomerSelection,
            login,
            clearSameDeviceSession: () => sessionStorage.removeItem(SAME_DEVICE_SESSION_KEY)
        });
    };

    // Keep refs in sync with state
    useEffect(() => { showCustomerSelectionRef.current = showCustomerSelection; }, [showCustomerSelection]);
    useEffect(() => { isTimeUpRef.current = isTimeUp; }, [isTimeUp]);
    useEffect(() => { authStartTimeRef.current = authStartTime; }, [authStartTime]);
    useEffect(() => { noAccountFoundRef.current = noAccountFound; }, [noAccountFound]);
    useEffect(() => { isSameDeviceFlowRef.current = isSameDeviceFlow; }, [isSameDeviceFlow]);

    // Resume the subscription when the user returns from the BankID app
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsSubscriptionPaused(document.hidden);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // Function to retry authentication when SSE stream ends
    const retryAuthentication = () => {
        if (
            isTimeUpRef.current ||
            showCustomerSelectionRef.current ||
            noAccountFoundRef.current ||
            autoLoginStartedRef.current
        ) return;

        // Don't create a new session during same-device flow — the user is in the BankID app
        // and will return via the returnUrl redirect. Creating a new session here would cause
        // the subscription to listen to the wrong session ID.
        if (isSameDeviceFlowRef.current) return;

        // Check if we still have time left
        if (authStartTimeRef.current) {
            const elapsed = Math.floor((Date.now() - authStartTimeRef.current) / 1000);
            const remaining = AUTH_SESSION_DURATION_SECONDS - elapsed;

            if (remaining > 0) {
                if (retryTimeoutRef.current) return;
                retryTimeoutRef.current = setTimeout(() => {
                    retryTimeoutRef.current = null;
                    if (
                        !isTimeUpRef.current &&
                        !showCustomerSelectionRef.current &&
                        !noAccountFoundRef.current &&
                        !autoLoginStartedRef.current &&
                        !isSameDeviceFlowRef.current
                    ) {
                        requestBankIdAuth({ key: bankIdKey })?.catch(() => undefined);
                    }
                }, 1000); // Wait 1 second before retrying
            }
        }
    };

    // Restore a pending same-device session when Android reloads the page after BankID
    useEffect(() => {
        // TODO: remove this
        //alert(window.navigator.userAgent);
        const saved = sessionStorage.getItem(SAME_DEVICE_SESSION_KEY);
        if (!saved) return;
        try {
            const { id, startTime, tokens } = JSON.parse(saved) as StoredSameDeviceSession;
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const remaining = AUTH_SESSION_DURATION_SECONDS - elapsed;
            if (remaining > 0) {
                setRestoredSessionId(id);
                setAuthStartTime(startTime);
                setShowBankIDAuth(true);
                isSameDeviceFlowRef.current = true;
                setIsSameDeviceFlow(true);
                setTimeLeft(remaining);
                if (tokens?.length) {
                    handleAuthenticationTokens(tokens);
                }
            } else {
                // Session expired while in BankID app
                sessionStorage.removeItem(SAME_DEVICE_SESSION_KEY);
            }
        } catch {
            sessionStorage.removeItem(SAME_DEVICE_SESSION_KEY);
        }
    }, []);

    // Get the bankID-ID to use in subscription.
    const [createBankIDAuth, { data: bankIdData, loading: bankIdLoading, error: bankIdError, reset: resetBankIdAuth }] = useMutation<CreateBankIDAuthResult, TokenByBankIdInput>(BANKID_MUTATION);

    const requestBankIdAuth = (input: TokenByBankIdInput['input']) => {
        if (bankIdRequestInFlightRef.current) return null;

        bankIdRequestInFlightRef.current = true;
        setIsBankIdRequestInFlight(true);

        return createBankIDAuth({ variables: { input } }).finally(() => {
            bankIdRequestInFlightRef.current = false;
            setIsBankIdRequestInFlight(false);
        });
    };

    // The active subscription session ID — prefer mutation result, fall back to restored session
    const activeSessionId = bankIdData?.tokenByBankId.bankIdResponse.id ?? restoredSessionId;

    // Subscribe to authentication updates
    const { data: authData, loading: authLoading, error: authError } = useSubscription<SubscriptionData>(
        AUTH_SUBSCRIPTION,
        {
            variables: {
                input: activeSessionId ? { id: activeSessionId } : undefined
            },
            skip: !activeSessionId || isSubscriptionPaused,
            fetchPolicy: 'no-cache', // Don't cache subscription data
            errorPolicy: 'all', // Continue receiving data even if there are errors
            onComplete: () => {
                // A hidden-page completion is an app switch, not a session failure
                if (!document.hidden && !isSubscriptionPaused) {
                    retryAuthentication();
                }
            }
        }
    );

    // Function to start authentication process
    const startAuthentication = () => {
        autoLoginStartedRef.current = false;
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        resetBankIdAuth();
        sessionStorage.removeItem(SAME_DEVICE_SESSION_KEY);
        setRestoredSessionId(null);
        setShowBankIDAuth(true);
        isSameDeviceFlowRef.current = false;
        setIsSameDeviceFlow(false);
        setNoAccountFound(false);
        setBankIdLaunchError(null);
        setTimeLeft(AUTH_SESSION_DURATION_SECONDS);
        setIsTimeUp(false);
        setShowCustomerSelection(false);
        setSelectedCustomer(null);
        setAvailableTokens([]);
        setLoggedInIdentifier(null);
        setAuthStartTime(Date.now());
        lastSameDeviceTokenRef.current = null;

        requestBankIdAuth({
            key: bankIdKey,  // TODO: replace this with server variables
            // No returnUrl — BankID should not redirect the scanning device's browser
        })?.catch(() => {
            // Optional: surface error state via UI if needed
        });
    };

    // Handler for BankID login button click
    const handleBankIDLoginClick = () => {
        startAuthentication();
    };

    // Countdown timer effect
    useEffect(() => {
        if (authStartTime && showBankIDAuth && !showCustomerSelection && !isTimeUp) {
            const interval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - authStartTime) / 1000);
                const remaining = Math.max(0, AUTH_SESSION_DURATION_SECONDS - elapsed);

                setTimeLeft(remaining);

                if (remaining === 0) {
                    setIsTimeUp(true);
                    clearInterval(interval);
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [authStartTime, showBankIDAuth, showCustomerSelection, isTimeUp]);

    // Fetch the logged-in person's identifier using the first available token
    // so we can determine which customer account is "owned" by the authenticated person
    useEffect(() => {
        if (!showCustomerSelection || availableTokens.length === 0 || loggedInIdentifier !== null) return;

        const firstToken = availableTokens[0].token;
        fetch(GRAPHQL_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${firstToken}`,
            },
            body: JSON.stringify({
                query: `query { currentUserProfile { identifier } }`
            }),
        })
            .then(res => res.json())
            .then(result => {
                const identifier = result?.data?.currentUserProfile?.identifier ?? null;
                setLoggedInIdentifier(identifier);
            })
            .catch(() => {
                // If this fails silently, labels just won't show — not a blocking error
            });
    }, [showCustomerSelection, availableTokens, loggedInIdentifier]);

    // Watch for BankID mutation data changes
    useEffect(() => {
        // Nothing to do here yet
    }, [bankIdData, bankIdLoading, bankIdError]);

    // Watch for authentication subscription updates
    useEffect(() => {
        let isActive = true;
        let retryTimeout: ReturnType<typeof setTimeout>;

        const restartAuth = () => {
            if (!isActive || isTimeUp || noAccountFoundRef.current || autoLoginStartedRef.current) return;

            // Clear any existing timeout
            if (retryTimeout) {
                clearTimeout(retryTimeout);
            }

            // Use the retry function with a small delay
            retryTimeout = setTimeout(() => {
                if (isActive && !autoLoginStartedRef.current) {
                    retryAuthentication();
                }
            }, 1000);
        };

        if (authLoading) {
            // Loading
        }

        if (authError) {
            // Log subscription error details for debugging
            const errorMsg = authError.message || 'Unknown subscription error';
            const isBackgroundedFailure = isBackgroundedSseFailure(authError);

            if (!isBackgroundedFailure) {
                console.debug('[BankID] Subscription error detected:', errorMsg, authError);
            }

            if (!isTimeUp && !isBackgroundedFailure) {
                // Check if error is SSE stream-related (timeout, connection drop)
                const isNetworkError = errorMsg.toLowerCase().includes('load failed') ||
                    errorMsg.toLowerCase().includes('stream') ||
                    errorMsg.toLowerCase().includes('timeout') ||
                    errorMsg.toLowerCase().includes('connection');

                if (isNetworkError) {
                    console.log('[BankID] Network error detected, attempting automatic recovery...');
                }
                // Retry regardless of error type — any subscription failure should attempt recovery
                restartAuth();
            }
        }

        if (authData?.onAuthenticationUpdate) {
            const update = authData.onAuthenticationUpdate;
            const hint = update.details?.hint;
            // Check for conditions that require a refresh
            if (hint === 'startFailed') {
                restartAuth();
            } else if (hint === 'expiredQrCode') {
                restartAuth();
            } else if (hint === 'noAccountFound') {
                setNoAccountFound(true);
            } else if (hint === 'outstandingTransaction') {
                // This is the normal state when waiting for user to scan
            } else if (update.tokens && update.tokens.length > 0) {
                console.info('[BankID] Authentication token metadata:', update.tokens.map(token => ({
                    hasAccessToken: Boolean(token.token),
                    hasRefreshToken: Boolean(token.refreshToken),
                    accessTokenExpiresAt: token.tokenExpiration,
                    refreshTokenExpiresAt: token.refreshTokenExpiration || null
                })));

                // Keep same-device authentication data across the iOS returnUrl reload
                // until a token has been persisted by login().
                const savedSession = sessionStorage.getItem(SAME_DEVICE_SESSION_KEY);
                if (savedSession) {
                    try {
                        const session = JSON.parse(savedSession) as StoredSameDeviceSession;
                        sessionStorage.setItem(SAME_DEVICE_SESSION_KEY, JSON.stringify({
                            ...session,
                            tokens: update.tokens
                        }));
                    } catch {
                        // Leave the live subscription result available for this render.
                    }
                }
                handleAuthenticationTokens(update.tokens);
            }
        }

        // Cleanup function
        return () => {
            isActive = false;
            if (retryTimeout) {
                clearTimeout(retryTimeout);
            }
        };
    }, [authData, authLoading, authError, createBankIDAuth]);

    // Get both mutation QR and subscription QR codes
    const mutationQrCode = bankIdData?.tokenByBankId?.bankIdResponse?.firstQrCode;
    const subscriptionQrCode = authData?.onAuthenticationUpdate?.details?.qrCode;

    // Get the current token (priority: mutation token, then look for subscription updates)
    const currentToken = sanitizeAutoStartToken(bankIdData?.tokenByBankId?.bankIdResponse?.autoStartToken);
    const blurQrCode = !subscriptionQrCode && !mutationQrCode;

    // Visible BankID app link for mobile — used as a fallback if auto-launch doesn't work
    const isMobileDevice = isMobileUserAgent(navigator.userAgent, navigator.maxTouchPoints);
    const sameDeviceReturnUrl = getBankIdReturnUrl(
        navigator.userAgent,
        navigator.maxTouchPoints,
        window.location.href
    );
    const bankIdAppUrl = currentToken
        ? getBankIdAppUrl(
            currentToken,
            navigator.userAgent,
            navigator.maxTouchPoints,
            sameDeviceReturnUrl
        )
        : null; // Blur if we don't have a valid QR code from either source

    const qrCodeValue = (() => {
        // If we have subscription data with a QR code, use that (it updates regularly)
        if (subscriptionQrCode) {
            return subscriptionQrCode;
        }

        // Fallback to the initial QR code from the mutation
        if (mutationQrCode) {
            return mutationQrCode;
        }

        // Last resort fallback
        // We need a similar code so the first second when the mutation has not come back yet it will display a similar
        // QR code format as the one shown when an error occurs.
        const errorQRCode = 'bankid.7c3f8d2a-9b14-4e67-a8c2-3f9d5e7a1b4c.0.d2a4f89c3e7b1d6a5f8c2e9b4d7a3c6f1e8b5d2a9c4f7e1b8d3a6c9f2e5b7d4a1c3'
        return errorQRCode;
    })();

    // Handle customer selection
    const handleCustomerSelect = (token: BearerToken) => {
        setSelectedCustomer(token);
        setTimeout(() => {
            login(token);
            sessionStorage.removeItem(SAME_DEVICE_SESSION_KEY);
        }, 600);
    };

    // Handle final login with selected customer TODO: not used anymore since we auto-login on customer select, but keeping this function in case we want to add a confirmation step in the future
    const handleLogin = () => {
        if (selectedCustomer) {
            // Use the new authentication system
            login(selectedCustomer);

            // The app will automatically redirect to <App /> due to auth state change
        }
    };

    // Reset authentication (start over)
    const handleRestart = () => {
        startAuthentication();
    };

    // Go back to initial state
    const handleGoBack = () => {
        autoLoginStartedRef.current = false;
        // Clear any pending retry attempts so it is not trying to refresh the session in the background while the user is trying to log in again
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        resetBankIdAuth();
        sessionStorage.removeItem(SAME_DEVICE_SESSION_KEY);
        setRestoredSessionId(null);
        setShowBankIDAuth(false);
        isSameDeviceFlowRef.current = false;
        setIsSameDeviceFlow(false);
        setNoAccountFound(false);
        setBankIdLaunchError(null);
        setShowCustomerSelection(false);
        showCustomerSelectionRef.current = false;
        setSelectedCustomer(null);
        setAvailableTokens([]);
        setIsTimeUp(false);
        setAuthStartTime(null);
        setTimeLeft(AUTH_SESSION_DURATION_SECONDS);
        lastSameDeviceTokenRef.current = null;
        setLoggedInIdentifier(null);
    };

    // Format time for display
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const launchBankIdApp = useCallback((token?: string | null, returnUrl?: string) => {
        const sanitizedToken = sanitizeAutoStartToken(token);
        if (!sanitizedToken) {
            return;
        }

        setBankIdLaunchError(null);
        const isMobile = isMobileUserAgent(navigator.userAgent, navigator.maxTouchPoints);

        if (isMobile) {
            // Mobile: use universal links (https://app.bankid.com) so iOS/Android
            // opens the BankID app natively.
            // iOS returns through the returnUrl sent with the same-device request.
            // IMPORTANT: element must be appended to the DOM before clicking, otherwise
            // Chrome Android treats it as a popup and may open a new tab.
            const link = document.createElement('a');
            // Selected Android browsers use the custom scheme when their app-link support is unreliable.
            link.href = getBankIdAppUrl(
                sanitizedToken,
                navigator.userAgent,
                navigator.maxTouchPoints,
                returnUrl
            );
            link.referrerPolicy = 'origin';
            link.style.display = 'none';
            document.body.appendChild(link);

            // Detect if the app launch failed by checking if page loses focus.
            // Listen to both 'blur' and 'visibilitychange' since different browsers
            // (e.g. Samsung Internet) may only fire one of them.
            let appLaunched = false;

            const clearDetection = () => {
                clearTimeout(timeout);
                window.removeEventListener('blur', handleBlur);
                document.removeEventListener('visibilitychange', handleVisibility);
            };

            const handleBlur = () => {
                appLaunched = true;
                clearDetection();
            };

            const handleVisibility = () => {
                if (document.hidden) {
                    appLaunched = true;
                    clearDetection();
                }
            };

            // Give the browser 2 seconds to launch the app before showing an error
            const timeout = setTimeout(() => {
                clearDetection();
                if (!appLaunched) {
                    setBankIdLaunchError(
                        'BankID-appen är inte installerad på denna enhet. ' +
                        'Försök igen eller använd QR-koden för att logga in från en annan enhet.'
                    );
                }
            }, 2000);

            window.addEventListener('blur', handleBlur);
            document.addEventListener('visibilitychange', handleVisibility);

            link.click();
            document.body.removeChild(link);
        } else {
            // Desktop: use bankid:// custom URL scheme
            // Detect if the scheme handler is installed by checking if the page loses focus.
            let appLaunched = false;

            const clearDetection = () => {
                clearTimeout(timeout);
                window.removeEventListener('blur', handleBlur);
                document.removeEventListener('visibilitychange', handleVisibility);
            };

            const handleBlur = () => {
                appLaunched = true;
                clearDetection();
            };

            const handleVisibility = () => {
                if (document.hidden) {
                    appLaunched = true;
                    clearDetection();
                }
            };

            const timeout = setTimeout(() => {
                clearDetection();
                if (!appLaunched) {
                    setBankIdLaunchError(
                        'BankID-appen är inte installerad på denna dator. ' +
                        'Installera BankID eller använd QR-koden för att logga in från en annan enhet.'
                    );
                }
            }, 2000);

            window.addEventListener('blur', handleBlur);
            document.addEventListener('visibilitychange', handleVisibility);

            try {
                window.location.href = `bankid:///?autostarttoken=${sanitizedToken}&redirect=null`;
            } catch (error) {
                clearDetection();
                setBankIdLaunchError(
                    `Kunde inte öppna BankID: ${error instanceof Error ? error.message : 'Okänt fel'}`
                );
            }
        }
    }, []);

    const handleSameDeviceLogin = () => {
        autoLoginStartedRef.current = false;
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        resetBankIdAuth();
        sessionStorage.removeItem(SAME_DEVICE_SESSION_KEY);
        setRestoredSessionId(null);
        setShowBankIDAuth(true);
        isSameDeviceFlowRef.current = true;
        setIsSameDeviceFlow(true);
        setNoAccountFound(false);
        setBankIdLaunchError(null);
        setTimeLeft(AUTH_SESSION_DURATION_SECONDS);
        setIsTimeUp(false);
        setShowCustomerSelection(false);
        setSelectedCustomer(null);
        setAvailableTokens([]);
        setLoggedInIdentifier(null);
        setAuthStartTime(Date.now());
        lastSameDeviceTokenRef.current = null;

        const returnUrl = getBankIdReturnUrl(
            navigator.userAgent,
            navigator.maxTouchPoints,
            window.location.href
        );
        const request = requestBankIdAuth({
            key: bankIdKey,
            returnUrl,
        });

        request?.then((result) => {
            const token = sanitizeAutoStartToken(result.data?.tokenByBankId?.bankIdResponse?.autoStartToken);
            const sessionId = result.data?.tokenByBankId?.bankIdResponse?.id;
            if (token && sessionId) {
                // Persist session so we can re-subscribe when iOS/Android reloads this tab
                sessionStorage.setItem(SAME_DEVICE_SESSION_KEY, JSON.stringify({
                    id: sessionId,
                    startTime: Date.now()
                }));
                if (!isIOSDevice(navigator.userAgent, navigator.maxTouchPoints)) {
                    launchBankIdApp(token, returnUrl);
                }
            }
        }).catch(() => {
            // Optional: surface error state via UI if needed
        });
    };

    // Match logged-in person's identifier against a customer nationalId.
    // Company org numbers may omit the first 2 digits of the personal number
    // e.g. person: 199011261911, company: 9011261911
    const isOwnerMatch = (nationalId: string | null): boolean => {
        if (!loggedInIdentifier || !nationalId) return false;
        if (nationalId === loggedInIdentifier) return true;
        return loggedInIdentifier.endsWith(nationalId);
    };

    // Sort tokens: "Ditt konto" first, then alphabetical by name
    const sortedTokens = [...availableTokens].sort((a, b) => {
        const aIsOwner = isOwnerMatch(a.customer.nationalId);
        const bIsOwner = isOwnerMatch(b.customer.nationalId);

        if (aIsOwner && !bIsOwner) return -1;
        if (!aIsOwner && bIsOwner) return 1;

        // Both are owners or both are not, sort by name alphabetically
        return a.customer.name.localeCompare(b.customer.name);
    });

    const isSameDeviceWaiting = isSameDeviceFlow || isSameDeviceFlowRef.current;

    return (
        <div className="bankid-section-wrapper shadow-one">
            <div className="bankid-glass">
                <div className="bankid-glass__content">
                    {!showCustomerSelection && (
                        <h1>
                            <BankIDLogoBlack width={50} height={50} />
                            Mina sidor
                        </h1>
                    )}

                    {!showBankIDAuth ? (
                        // Initial welcome content before BankID authentication
                        <>
                            <div>
                                <div className="intro-text-with-logo">
                                    <BankIDLogoBlack width={70} height={70} />
                                    <p className="intro-text">
                                        På mina sidor hanterar du ditt medlemskap, ser dina transaktioner, hantera dina kort och mycket mer. För att komma igång, logga in med BankID.
                                    </p>
                                </div>

                                <div className="bankid-button-wrapper">
                                    <Button
                                        onClick={handleBankIDLoginClick}
                                        disabled={isBankIdRequestInFlight || bankIdLoading}
                                        className={'dashboard-button'}
                                    >
                                        <QrCodeIcon width="32" height="32" />
                                        BankID på annan enhet
                                    </Button>

                                    <button
                                        onClick={handleSameDeviceLogin}
                                        disabled={isBankIdRequestInFlight || bankIdLoading}
                                        className={'dashboard-button'}
                                    >
                                        <MobileIcon width="32" height="32" />
                                        BankID på denna enhet
                                    </button>
                                </div>

                                <div className="bankid-glass__info-text glare has-dark-bg">
                                    <h6>För företag</h6>
                                    <p>
                                        Har du ett aktiebolag och vill administrera ditt konto?
                                        Skicka in en ansökan om behörighetstilldelning.
                                    </p>
                                    <p>
                                        <strong>Ansök här: </strong><a href={import.meta.env.VITE_VERIFIED_URL_COMPANY || 'https://qstar.se/foretagskort/#kort'} target="_blank">Qstar - Företag</a>
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : (
                        // BankID authentication flow
                        <>
                            {/* Only show error if we have an error AND no data  TODO: remove inline styles */}
                            {(bankIdError && !bankIdData) && (
                                <div className="bankid-error-wrapper">
                                    <p>
                                        Ett fel uppstod vid inloggning.
                                    </p>
                                    <p>
                                        Error: {bankIdError.message}
                                    </p>
                                </div>
                            )}
                            {authError && !isBackgroundedSseFailure(authError) && (
                                <div className="bankid-error-wrapper">
                                    <p>
                                        Ett fel uppstod vid inloggning.
                                    </p>
                                    <p>
                                        Auth Error: {authError.message}
                                    </p>
                                </div>
                            )}

                            {isTimeUp ? (
                                // Timeout interface
                                <div className="timeout-wrapper">
                                    <p>
                                        BankID-sessionen har gått ut. Klicka på knappen nedan för att starta om inloggningen.
                                    </p>
                                    {isSameDeviceFlow ? (
                                        <button
                                            onClick={handleSameDeviceLogin}
                                            disabled={isBankIdRequestInFlight || bankIdLoading}
                                            className="dashboard-button"
                                        >
                                            <MobileIcon width="32" height="32" />
                                            BankID på denna enhet
                                        </button>
                                    ) : (
                                        <Button
                                            onClick={handleRestart}
                                            disabled={isBankIdRequestInFlight || bankIdLoading}
                                            className="Button body-text"
                                        >
                                            <QrCodeIcon width="32" height="32" />
                                            Starta om BankID-inloggning
                                        </Button>
                                    )}
                                </div>
                            ) : showCustomerSelection ? (
                                // Customer selection interface
                                <div className="success-login-wrapper">
                                    <h2 className="success-login">
                                        <span className="svg-icon-wrapper orange round">
                                            <AccountBoxIcon width="32" height="32" />
                                        </span>
                                        Välj konto nedan
                                    </h2>

                                    <div className="customer-selection-wrapper">
                                        {sortedTokens.map((token, index) => (
                                            <div
                                                key={index}
                                                className={classNames('customer-option',
                                                    { 'selected': selectedCustomer === token }
                                                )}
                                                onClick={() => handleCustomerSelect(token)}
                                            >
                                                <div className="customer-info">
                                                    <span className="customer-name">{token.customer.name}</span>
                                                    <span className="customer-number">Kundnummer: {token.customer.customerNumber}</span>
                                                    <span className="customer-national-id">{token.customer.isCompany ? 'Organisationsnummer' : 'Personnummer'}: {token.customer.nationalId}</span>
                                                </div>
                                                <div className="customer-labels">
                                                    <span className={classNames(
                                                        'customer-label',
                                                        {
                                                            'company': token.customer.isCompany,
                                                            'private': !token.customer.isCompany
                                                        })}
                                                    >
                                                        {token.customer.isCompany ? 'Företag' : 'Privat'}
                                                    </span>
                                                    {loggedInIdentifier && isOwnerMatch(token.customer.nationalId) && (
                                                        <span className="customer-label owner">
                                                            Ditt konto
                                                        </span>
                                                    )}
                                                    {loggedInIdentifier && !token.customer.isCompany && !isOwnerMatch(token.customer.nationalId) && (
                                                        <span className="customer-label managed">
                                                            Tillgång till annans konto
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className='button-group-customer-selection'>
                                        <button
                                            onClick={handleGoBack}
                                            className="Button button-cancel body-text"
                                        >
                                            Avbryt
                                        </button>
                                    </div>
                                </div>
                            ) : noAccountFound ? (
                                // No account found screen
                                <div className="no-account">
                                    <p>Inget konto hittades kopplat till ditt BankID.</p>
                                    <p>Vill du läsa mer om medlemskap hos Qstar eller bli medlem läs mer om detta på länken nedan.</p>
                                    <div className="button-wrapper-no-account">
                                        <Link href="https://qstar.se/medlem/" openNewTab={true} className="Button dashboard-button body-text">
                                            Läs mer om medlemskap
                                            <ExternalLinkIcon />
                                        </Link>
                                        <button
                                            onClick={handleGoBack}
                                            className="Button button-cancel body-text"
                                        >
                                            Tillbaka
                                        </button>
                                    </div>

                                </div>
                            ) : isSameDeviceWaiting ? (
                                // Same-device waiting screen
                                <>
                                    {bankIdLaunchError && (
                                        <div className="bankid-error-wrapper">
                                            <p>
                                                {bankIdLaunchError}
                                            </p>
                                        </div>
                                    )}
                                    <div className="same-device-waiting">
                                        <span className="loader-zoom"></span>
                                        <p>Identifiera dig i BankID-appen för att fortsätta inloggningen</p>
                                        {isMobileDevice && !isBankIdRequestInFlight && bankIdAppUrl && (
                                            <a
                                                href={bankIdAppUrl}
                                                referrerPolicy="origin"
                                                className="link-text-bankid"
                                            >
                                                <MobileIcon width="32" height="32" />
                                                Öppna BankID-appen
                                            </a>
                                        )}
                                        <button
                                            onClick={handleGoBack}
                                            className="Button button-cancel body-text"
                                        >
                                            Avbryt
                                        </button>
                                    </div>
                                </>

                            ) : (
                                // QR Code interface
                                <>
                                    {bankIdLaunchError && (
                                        <div className="bankid-error-wrapper">
                                            <p>
                                                {bankIdLaunchError}
                                            </p>
                                        </div>
                                    )}
                                    <div className="login-instructions">
                                        <div className="left-side">
                                            <ol>
                                                <li>Öppna appen BankID i din mobil</li>
                                                <li>Klicka på Skanna QR-kod</li>
                                                <li>Rikta kameran mot QR-koden på skärmen</li>
                                                <li>Identifiera dig</li>
                                            </ol>
                                        </div>
                                        <div className="right-side">
                                            {authData?.onAuthenticationUpdate?.details?.hint === 'userSign' ? (
                                                <div className="bankid-user-sign">
                                                    <span className="bankid-square-one"></span>
                                                    <span className="bankid-square-two"></span>
                                                    <span className="bankid-square-three"></span>
                                                    <p>Slutför identifieringen i BankID-appen</p>
                                                </div>
                                            ) : (
                                                <div className={classNames(
                                                    "qr-code-container",
                                                    { "blur": blurQrCode })}>
                                                    <div className="login-pulse">
                                                        <QRCode
                                                            key={qrCodeValue}
                                                            size={256}
                                                            value={qrCodeValue}
                                                            viewBox={`0 0 256 256`}
                                                        />
                                                    </div>
                                                    <span className="loader-zoom white"></span>
                                                </div>
                                            )}

                                            {/* Countdown timer */}
                                            <div className="countdown-timer">
                                                <p>
                                                    Tid kvar: {formatTime(timeLeft)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="button-group-qrcode">
                                        {/* Open BankID on this device button */}
                                        <Button
                                            onClick={handleSameDeviceLogin}
                                            disabled={isBankIdRequestInFlight || bankIdLoading}
                                            className="dashboard-button"
                                        >
                                            <MobileIcon width="32" height="32" />
                                            BankID på denna enhet
                                        </Button>

                                        {/* Back button */}
                                        <button
                                            onClick={handleGoBack}
                                            className="Button button-cancel body-text"
                                        >
                                            Avbryt
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="img-wrapper">
                <img
                    alt="Qstar logga in, qstarlastbil på landsväg"
                    src="static/images/qstar-landsvag.jpg"
                    onLoad={e => (e.currentTarget as HTMLImageElement).classList.add('loaded')}
                />
            </div>
        </div>
    );
};

export default BankIDSection;